import Emitter from '@resources/js/helpers/Emitter'
import { format, sameDay, addDay } from '@formkit/tempo'
import { normalizeFlatpickrLocale } from '@resources/js/components/form/helpers/normalizeFlatpickrLocale'

const localeCode = shared.options.language.code || 'en'
const resolvedLocale = normalizeFlatpickrLocale(localeCode)
const DEFAULT_DATE_FORMAT = 'long'

const STORE_FORMATTERS = {
	checkIn: (date, dateFormat) => format(date, dateFormat, resolvedLocale),
	checkOut: (date, dateFormat) => format(date, dateFormat, resolvedLocale),
}

const GUESTS_LIMITS = {
	adults: { min: 1, max: 20 },
	children: { min: 0, max: 10 },
}

export default class FormPresenter extends Emitter {
	#view
	#store
	#dateFormat

	constructor(view, store, dateFormat = DEFAULT_DATE_FORMAT) {
		super()
		this.#view = view
		this.#store = store
		this.#dateFormat = dateFormat

		this.#init()
	}

	#init() {
		this.#syncViewWithStore()
		this.#initEvents()
	}

	#initEvents() {
		// Підписка на зміну дат у View
		this.#view.on('dates-changed', ({ checkIn, checkOut }) => {
			this.#updateStoreDates(checkIn, checkOut)
		})

		// Підписка на зміну кількості дорослих у View
		this.#view.on('adults-change', value => this.#handleAdultsCountChange(value))

		// Підписка на зміну кількості дітей у View
		this.#view.on('children-change', value =>
			this.#handleChildrenCountChange(value)
		)

		// Підписка на зміну віку дитини у View
		this.#view.on('child-age-change', data => {
			const { index, value } = data
			this.#store.addChildObj(index, value)
		})

		// Підписка на submit
		this.#view.on('form-submit', formData => {
			this.#handleSubmit(formData)
		})

		// Підписка на зміну в Store
		this.#store.on('change', ({ name, value }) => {
			this.#handleStoreChange(name, value)
		})

		// Підписка на зміну кількосьті дітей у Store
		this.#store.on('children-count-changed', count => {
			this.#view.updateChildrenAgeSelects(count)
		})

		// Підписка на додавання обʼєкту з віком дитини
		this.#store.on('child-object-add', ({ index, value }) => {
			this.#view.setChildAgeValue(index, value)
		})
	}

	updateAdultsCount(value) {
		const currentValue = this.#store.getProperty('adultsCount')

		if (
			currentValue === value ||
			!this.#validateCountForGuestType('adults', value)
		)
			return

		this.#store.setProperty(`adultsCount`, value)
	}

	updateChildrenCount(value) {
		const currentValue = this.#store.getProperty('childrenCount')

		if (
			currentValue === value ||
			!this.#validateCountForGuestType('children', value)
		)
			return

		this.#store.setProperty(`childrenCount`, value)

		if (currentValue > value) this.#store.removeChildObj()
	}

	#handleAdultsCountChange(value) {
		this.updateAdultsCount(value)
	}

	#handleChildrenCountChange(value) {
		this.updateChildrenCount(value)
	}

	#validateCountForGuestType(guestType, count) {
		const { min, max } = GUESTS_LIMITS[guestType]
		return count >= min && count <= max
	}

	#syncViewWithStore() {
		const checkIn = this.#store.getProperty('checkIn')
		const checkOut = this.#store.getProperty('checkOut')

		this.#updateViewDates(checkIn, checkOut)

		this.#updateViewGuests()
	}

	#updateViewDates(checkIn, checkOut) {
		this.#view.update({ checkIn: this.#formatDate(checkIn) })
		this.#view.update({ checkOut: this.#formatDate(checkOut) })
	}

	#checkIsSameDay(date1, date2) {
		return sameDay(date1, date2)
	}

	#resolveCheckOut(checkIn, checkOut) {
		if (checkIn && checkOut && this.#checkIsSameDay(checkIn, checkOut))
			return addDay(checkIn, 1)

		return checkOut
	}

	#handleStoreChange(name, value) {
		const formatter = STORE_FORMATTERS[name]

		const updateObj = {
			[name]: formatter ? formatter(value, this.#dateFormat) : value,
		}

		this.#view.update(updateObj)
	}

	#formatDate(date) {
		try {
			return format(date, this.#dateFormat, resolvedLocale)
		} catch (e) {
			if (!(e instanceof RangeError)) throw e
			// Fallback для пристроїв з некоректною системною таймзоною (напр. Etc/Unknown)
			return new Intl.DateTimeFormat(resolvedLocale, {
				timeZone: 'UTC',
				day: 'numeric',
				month: 'short',
				year: 'numeric',
			}).format(new Date(date))
		}
	}

	#updateStoreDates(checkIn, checkOut) {
		const resolvedCheckOut = this.#resolveCheckOut(checkIn, checkOut)

		if (
			checkIn &&
			!this.#checkIsSameDay(checkIn, this.#store.getProperty('checkIn'))
		) {
			this.#store.setProperty('checkIn', checkIn)
		}

		if (
			resolvedCheckOut &&
			!this.#checkIsSameDay(
				resolvedCheckOut,
				this.#store.getProperty('checkOut')
			)
		) {
			this.#store.setProperty('checkOut', resolvedCheckOut)
		}

		// Синхронізуємо календар, якщо checkOut було зсунуто через однаковий день
		if (resolvedCheckOut !== checkOut)
			this.#view.setCalendarDates([checkIn, resolvedCheckOut])
	}

	#handleSubmit() {
		this.emit('form-submit', this.#store.getData())
	}

	#updateViewGuests() {
		const childrenCount = this.#store.getProperty('childrenCount')

		this.#view.update({ adultsCount: this.#store.getProperty('adultsCount') })
		this.#view.update({ childrenCount })

		const children = this.#store.getProperty('children')

		this.#view.updateChildrenAgeSelects(childrenCount)
		children.forEach(({ age }, index) => this.#view.setChildAgeValue(index, age))
	}
}
